import { ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import TextWinds from '../../../components/common/TextWinds';
import HFbutton from '../../../components/common/input/HFbutton';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import BasicInfoSection from '../../../components/student/profile/BasicInfoSection';
import ParentsSection from '../../../components/student/profile/ParentsSection';
import { StudentProfileForm } from '../../../constants/types/student-profile-form';
import {
  getStudentProfile,
  putStudentBasicInfo,
  putStudentGuardianInfo,
} from '../../../api/utils/student/profile/apis';
import {
  normalizeParents,
  StudentMyInfoForm,
  toStudentMyInfoForm,
} from '../../../api/utils/student/profile/mapper';

import { StudentProfileContext } from '../StudentProfileContext';
import ClassCodeSection from '../../../components/student/profile/ClassCodeSection';
import PasswordSection from '../../../components/student/profile/PasswordSection';
import { StudentProfile } from '../../../constants/types/student/profile/type';
import { useSectionEditForm } from '../../../hooks/student/useSectionEditForm';

/** 아코디언 카드 한 칸. 카드 단위로 펼치고 수정한다. */
type CardKey = 'basic' | 'parents' | 'password' | 'class';

/** 내 정보 폼(기본 정보/보호자)으로 다루는 카드 */
type FormCardKey = Extract<CardKey, 'basic' | 'parents'>;

/**
 * 카드별 수정 대상 필드.
 * - 카드끼리 겹치는 필드가 없어야 한다. (한 카드 저장이 다른 카드 값을 덮어쓰지 않도록)
 * - 검증(trigger) 범위와 변경 여부 비교 범위로 함께 쓰인다.
 */
const CARD_FIELDS: Record<FormCardKey, (keyof StudentMyInfoForm)[]> = {
  basic: ['englishName', 'birthday', 'gender'],
  parents: ['parents'],
};

/**
 * 학생 > 내 정보 화면.
 * 영역별 수정 방식은 LMS 학생 상세 정보와 같고, 각 영역을 접었다 펴는 카드로 보여준다.
 */
const StudentProfilePage = () => {
  const { setStudentProfile } = useContext(StudentProfileContext);
  /** 다른 화면(클래스 목록 등)에서 특정 카드를 펼친 채로 넘어올 때 쓴다. */
  const openSection = (useLocation().state as { openSection?: CardKey } | null)
    ?.openSection;
  /** 현재 수정 중인 카드. null이면 전체 조회 상태 */
  const [editCard, setEditCard] = useState<CardKey | null>(null);
  /** 펼쳐진 카드. 처음에는 모두 접어두고 여러 개를 함께 펼칠 수 있다. */
  const [openCards, setOpenCards] = useState<CardKey[]>(
    openSection ? [openSection] : [],
  );
  const [oldProfile, setOldProfile] = useState<StudentProfile>();
  const [loadFailed, setLoadFailed] = useState(false);
  const uid = localStorage.getItem('userId') ?? undefined;

  // LMS 학생 상세와 입력 컴포넌트를 공유하므로 폼 타입도 같은 것을 쓴다.
  // 이 화면은 그중 기본 정보/보호자만 다루고 나머지 필드는 채우지 않는다.
  const {
    setValue,
    getValues,
    trigger,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm<StudentProfileForm>({
    defaultValues: {
      parents: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'parents',
  });

  const init = () => {
    setLoadFailed(false);
    getStudentProfile()
      .then((data) => {
        setOldProfile(data);
        // 비밀번호/클래스 카드도 같은 값을 보도록 컨텍스트를 함께 갱신한다.
        setStudentProfile(data);
        reset(toStudentMyInfoForm(data));
      })
      .catch((err) => {
        console.error(err);
        setLoadFailed(true);
      });
  };

  useEffect(() => {
    init();
  }, []);

  /** 변경 여부 비교에 쓸 카드의 값. 저장할 때와 같은 형태로 맞춰서 비교한다. */
  const cardValues = (form: StudentMyInfoForm, card: FormCardKey) =>
    CARD_FIELDS[card].map((field) =>
      // 보호자 연락처는 하이픈 유무만 다를 수 있으므로 저장 형태로 맞춘다.
      field === 'parents' ? normalizeParents(form.parents ?? []) : form[field],
    );

  /** 조회한 값과 비교해 이 카드에 바뀐 값이 있는지 확인한다. */
  const isCardChanged = (card: FormCardKey) => {
    if (!oldProfile) return false;
    const next = cardValues(getValues(), card);
    const prev = cardValues(toStudentMyInfoForm(oldProfile), card);
    return JSON.stringify(next) !== JSON.stringify(prev);
  };

  /** 카드별 저장 요청 */
  const saveCard = (card: FormCardKey) => {
    const form = getValues();
    if (card === 'basic') {
      // 이름은 수정할 수 없으므로 조회한 값을 그대로 함께 보낸다.
      return putStudentBasicInfo({
        name: oldProfile?.name ?? '',
        englishName: form.englishName ?? '',
        birthday: form.birthday ?? '',
        gender: form.gender as 'MALE' | 'FEMALE',
      });
    }

    const [guardian] = normalizeParents(form.parents ?? []);
    return putStudentGuardianInfo({
      name: guardian?.name ?? '',
      phone: guardian?.phone ?? null,
      gender: guardian?.gender as 'MALE' | 'FEMALE',
      birthday: guardian?.birthday,
    });
  };

  const editForm = useSectionEditForm({
    fields: CARD_FIELDS,
    form: { getValues, setValue, trigger, watch, reset },
    getOrigin: () => (oldProfile ? toStudentMyInfoForm(oldProfile) : undefined),
    save: async (card) => {
      if (!isCardChanged(card)) return false;
      await saveCard(card);
      return true;
    },
    onSaved: init,
    // 비밀번호/클래스 카드와 편집 대상을 공유해 한 번에 한 카드만 열리게 한다.
    editSection:
      editCard === 'basic' || editCard === 'parents' ? editCard : null,
    onEditSectionChange: setEditCard,
  });

  /** 제목 줄 클릭. 수정 중인 카드는 입력이 사라지지 않도록 접지 않는다. */
  const toggleCard = (card: CardKey) => {
    if (editCard === card) return;

    setOpenCards((cards) =>
      cards.includes(card)
        ? cards.filter((item) => item !== card)
        : [...cards, card],
    );
  };

  /** 카드를 펼치면서 수정을 시작한다. (접힌 채로 수정되는 일이 없도록) */
  const startEdit = (card: CardKey) => {
    setOpenCards((cards) => (cards.includes(card) ? cards : [...cards, card]));
    if (card === 'basic' || card === 'parents') {
      editForm.startEdit(card);
    } else {
      setEditCard(card);
    }
  };

  /** 모든 카드에 공통인 펼침/수정 상태 */
  const cardState = (card: CardKey) => ({
    isOpen: openCards.includes(card),
    onToggle: () => toggleCard(card),
    isEditing: editCard === card,
    // 다른 카드를 수정하는 중이면 이 카드는 수정을 시작할 수 없다.
    disabled: editCard !== null && editCard !== card,
    onEdit: () => startEdit(card),
  });

  /** 기본 정보/보호자 카드의 헤더에 넘길 props */
  const formCardProps = (card: FormCardKey) => ({
    ...cardState(card),
    isSaving: editCard === card && editForm.isSaving,
    onCancel: () => editForm.cancel(card),
    onSave: () => editForm.submit(card),
  });

  /** 저장을 스스로 처리하는 카드(비밀번호/클래스)에 넘길 props */
  const ownSaveCardProps = (card: CardKey) => ({
    ...cardState(card),
    onClose: () => setEditCard(null),
  });

  // 조회 실패 / 조회 중에는 카드 대신 상태만 보여준다.
  if (loadFailed || !oldProfile) {
    return (
      <ProfileLayout>
        <div className="rounded-lg bg-white p-4 shadow-card">
          {loadFailed ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-4">
              <TextWinds type="content_body2" color="gray7">
                내 정보를 불러오지 못했습니다.
              </TextWinds>
              <div className="w-[160px]">
                <HFbutton height={36} onClick={init}>
                  다시 시도
                </HFbutton>
              </div>
            </div>
          ) : (
            <LoadingSpinner message="데이터 조회중" />
          )}
        </div>
      </ProfileLayout>
    );
  }

  return (
    <ProfileLayout>
      <div className="flex flex-col gap-4">
        <ProfileCard>
          <form>
            <BasicInfoSection
              control={control}
              errors={errors}
              section={formCardProps('basic')}
              uid={uid}
              name={oldProfile.name}
            />
          </form>
        </ProfileCard>

        <ProfileCard>
          <form>
            <ParentsSection
              control={control}
              errors={errors}
              section={formCardProps('parents')}
              fields={fields}
              append={append}
              remove={remove}
              // INFO: 학생 보호자 저장 API(putStudentGuardianInfo)가 단일 객체만 받으므로 1명으로 제한합니다.
              maxParents={1}
            />
          </form>
        </ProfileCard>

        {/* 클래스 정보 */}
        <ProfileCard>
          <ClassCodeSection section={ownSaveCardProps('class')} />
        </ProfileCard>

        {/* 비밀번호 변경 */}
        <ProfileCard>
          <PasswordSection section={ownSaveCardProps('password')} />
        </ProfileCard>
      </div>
    </ProfileLayout>
  );
};

/** 아코디언 카드 한 칸의 껍데기 */
const ProfileCard = ({ children }: { children: ReactNode }) => (
  <div className="rounded-lg bg-white p-4 shadow-card xl:p-5">{children}</div>
);

/** 헤더 + 본문 공통 레이아웃. 조회 중/실패 화면도 같은 자리에 보여준다. */
const ProfileLayout = ({ children }: { children: ReactNode }) => (
  <div className="w-full bg-gray0 pb-10 pt-6">
    <div className="mx-auto flex w-full max-w-[900px] flex-col px-5">
      <header className="mb-5 min-w-0">
        <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
          내 정보
        </TextWinds>
        <TextWinds type="content_body3">
          각 항목별로 내 정보를 확인하고 수정할 수 있어요.
        </TextWinds>
      </header>
      {children}
    </div>
  </div>
);

export default StudentProfilePage;
